import { Box, Typography, Button, TextField, Stack, IconButton, Checkbox } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { addProductFeatures, updateProductFeatures, deleteProductFeatures } from "../../../../../services/Admin/ProductsMediaAndDetailsApi";

const ProductFeaturesTab = ({ productId, desc_ar, desc_en, features = [], onSuccess }) => {
  const [newFeatures, setNewFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingValues, setEditingValues] = useState({ title_ar: "", title_en: "", desc_ar: "", desc_en: "" });
  const [rowSaving, setRowSaving] = useState(false);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState([]);

  const handleAddRow = () => {
    setNewFeatures((s) => [...s, { title_ar: "", title_en: "", desc_ar: "", desc_en: "" }]);
  };

  const handleRemove = (index) => {
    setNewFeatures((s) => s.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    setNewFeatures((s) => s.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const handleSubmit = async () => {
    setError("");
    if (!productId) {
      setError("productId مطلوب.");
      return;
    }
    if (!newFeatures.length) {
      setError("أضف ميزة واحدة على الأقل.");
      return;
    }
    setLoading(true);
    try {
      await addProductFeatures(productId, newFeatures, { product_id: productId });
      setNewFeatures([]);
      if (onSuccess) await onSuccess();
    } catch (err) {
      setError(err?.message || "فشل إضافة المميزات");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    if (!id) return;
    setSelectedFeatureIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const handleDeleteSingle = async (id) => {
    setError("");
    if (!productId) {
      setError("productId مطلوب.");
      return;
    }
    if (!id) return;
    setLoading(true);
    try {
      await deleteProductFeatures([id], { product_id: productId });
      setSelectedFeatureIds((s) => s.filter((x) => x !== id));
      if (onSuccess) await onSuccess();
    } catch (err) {
      setError(err?.message || "فشل حذف الميزة");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    setError("");
    if (!productId) {
      setError("productId مطلوب.");
      return;
    }
    if (!selectedFeatureIds.length) {
      setError("اختر ميزة واحدة على الأقل للحذف.");
      return;
    }
    setLoading(true);
    try {
      await deleteProductFeatures(selectedFeatureIds, { product_id: productId });
      setSelectedFeatureIds([]);
      if (onSuccess) await onSuccess();
    } catch (err) {
      setError(err?.message || "فشل حذف المميزات");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: 600, mb: 1 }}>الوصف</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {desc_ar || desc_en || "لا يوجد وصف"}
      </Typography>

      <Typography sx={{ fontWeight: 600, mb: 1 }}>المميزات</Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Button onClick={handleBulkDelete} disabled={!selectedFeatureIds.length || !productId || loading} size="small" variant="outlined" color="error">
          حذف المحدد ({selectedFeatureIds.length})
        </Button>
      </Stack>
      {(features || []).map((f) => {
        const isEditing = editingId === f.id;
        return (
          <Box key={f.id} sx={{ mb: 1 }}>
            {!isEditing ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Checkbox checked={selectedFeatureIds.includes(f.id)} onChange={() => toggleSelect(f.id)} disabled={!f.id} size="small" />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>{f.title_ar || f.title_en}</Typography>
                  <Typography variant="body2" color="text.secondary">{f.desc_ar || f.desc_en}</Typography>
                </Box>
                <IconButton size="small" onClick={() => {
                  setEditingId(f.id);
                  setEditingValues({
                    title_ar: f.title_ar ?? "",
                    title_en: f.title_en ?? "",
                    desc_ar: f.desc_ar ?? "",
                    desc_en: f.desc_en ?? "",
                  });
                }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDeleteSingle(f.id)} aria-label="delete-existing" disabled={!f.id || loading || !productId}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField size="small" label="العنوان بالعربي" value={editingValues.title_ar} onChange={(e) => setEditingValues((p) => ({ ...p, title_ar: e.target.value }))} />
                <TextField size="small" label="العنوان بالإنجليزي" value={editingValues.title_en} onChange={(e) => setEditingValues((p) => ({ ...p, title_en: e.target.value }))} />
                <TextField size="small" label="الوصف بالعربي" value={editingValues.desc_ar} onChange={(e) => setEditingValues((p) => ({ ...p, desc_ar: e.target.value }))} />
                <TextField size="small" label="الوصف بالإنجليزي" value={editingValues.desc_en} onChange={(e) => setEditingValues((p) => ({ ...p, desc_en: e.target.value }))} />
                <IconButton size="small" onClick={async () => {
                  setRowSaving(true);
                  setError("");
                  try {
                    const payload = [{
                      id: f.id,
                      title_ar: editingValues.title_ar,
                      title_en: editingValues.title_en,
                      desc_ar: editingValues.desc_ar,
                      desc_en: editingValues.desc_en,
                    }];
                    await updateProductFeatures(payload, { product_id: productId });
                    setEditingId(null);
                    setEditingValues({ title_ar: "", title_en: "", desc_ar: "", desc_en: "" });
                    if (onSuccess) await onSuccess();
                  } catch (err) {
                    setError(err?.message || "فشل تحديث الميزة");
                  } finally {
                    setRowSaving(false);
                  }
                }} disabled={rowSaving || !productId}>
                  <SaveIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => { setEditingId(null); setEditingValues({ title_ar: "", title_en: "", desc_ar: "", desc_en: "" }); }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          </Box>
        );
      })}

      {/* إضافة مميزات جديدة */}
      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Button startIcon={<AddIcon />} onClick={handleAddRow} size="small" variant="contained">
            إضافة صف
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !newFeatures.length || !productId} size="small" variant="outlined">
            {loading ? "جارٍ..." : "حفظ المميزات"}
          </Button>
        </Stack>

        {error ? <Typography color="error" sx={{ mb: 1 }}>{error}</Typography> : null}

        {newFeatures.map((r, idx) => (
          <Stack key={idx} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TextField size="small" label="العنوان بالعربي" value={r.title_ar} onChange={(e) => handleChange(idx, "title_ar", e.target.value)} />
            <TextField size="small" label="العنوان بالإنجليزي" value={r.title_en} onChange={(e) => handleChange(idx, "title_en", e.target.value)} />
            <TextField size="small" label="الوصف بالعربي" value={r.desc_ar} onChange={(e) => handleChange(idx, "desc_ar", e.target.value)} />
            <TextField size="small" label="الوصف بالإنجليزي" value={r.desc_en} onChange={(e) => handleChange(idx, "desc_en", e.target.value)} />
            <IconButton size="small" onClick={() => handleRemove(idx)} aria-label="remove">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

export default ProductFeaturesTab;
